import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type { ApiAvailabilityResponse } from "@/types/api";

/** Reservation slot grid for ``targetDate`` at the active location.
 *
 *  Mirrors the public ``/api/public/locations/availability/`` endpoint
 *  the guest site consumes, so the staff "new reservation" flow offers
 *  the same 15-min granularity instead of a hardcoded client grid.
 *  ``zoneId`` and ``features`` narrow the search the same way. */
export function useAvailability(params: {
  targetDate: string;
  pax: number;
  zoneId?: string;
  features?: string[];
  enabled?: boolean;
}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const locationId = useAuthStore((s) => s.locationId);
  const { targetDate, pax, zoneId, features, enabled = true } = params;

  return useQuery({
    queryKey: ["availability", locationId, targetDate, pax, zoneId, features],
    queryFn: async () => {
      const search = new URLSearchParams({
        target_date: targetDate,
        pax: String(pax),
      });
      if (zoneId) search.set("zone_id", zoneId);
      if (features?.length) search.set("features", features.join(","));
      return api
        .get(`locations/${locationId}/availability/?${search}`)
        .json<ApiAvailabilityResponse>();
    },
    enabled: isAuthenticated && !!locationId && !!targetDate && pax > 0 && enabled,
    staleTime: 30_000,
    // Keep the previous slot grid visible while the user clicks pax up/down
    // — otherwise the <select> blanks out on every tick.
    placeholderData: (prev) => prev,
  });
}
